import { ArrowDownRight, Sparkles, Layers, Download } from 'lucide-react';

const features = [
  { icon: Layers, title: 'Stack & Arrange', body: 'Drop multiple PNGs and JPGs. Move, resize, rotate—every piece on its own layer.' },
  { icon: Sparkles, title: 'Backgrounds, Your Way', body: 'Solid, gradient, image, or fully transparent. Make it yours in one click.' },
  { icon: Download, title: 'Export Crisp 5000×5000', body: 'Download as PNG (with transparency) or JPG. Pixel-perfect, every time.' },
];

export const Hero = ({ onStart }) => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain bg-canvas"
    >
      {/* Decorative tilted color blocks */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-12 top-24 h-44 w-44 border-2 border-ink bg-lilac shadow-brutLg rotate-[-8deg]" />
        <div className="absolute right-[6%] top-[14%] h-28 w-28 border-2 border-ink bg-mint shadow-brutLg rotate-[12deg]" />
        <div className="absolute left-[18%] bottom-[14%] h-20 w-32 border-2 border-ink bg-peach shadow-brutLg rotate-[-4deg]" />
        <div className="absolute right-[12%] bottom-[18%] h-24 w-24 rounded-full border-2 border-ink bg-sun shadow-brutLg" />
      </div>

      {/* nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-16 pt-6">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 border-2 border-ink bg-lilac shadow-brutSm rotate-[-6deg]" />
          <div className="h-9 w-9 border-2 border-ink bg-peach shadow-brutSm -ml-3 rotate-[6deg]" />
          <span className="ml-2 font-outfit font-black text-xl tracking-tight">StckrStckr</span>
        </div>
        <button
          data-testid="nav-launch-btn"
          onClick={onStart}
          className="neo-btn bg-paper px-4 py-2 text-sm"
        >
          Open Editor →
        </button>
      </nav>

      {/* hero content */}
      <div className="relative z-10 px-6 sm:px-10 lg:px-16 pt-16 lg:pt-24 pb-16 max-w-6xl">
        <span data-testid="hero-eyebrow" className="label-tiny inline-block bg-paper border-2 border-ink px-3 py-1 shadow-brutSm">
          A scrapbook for the internet
        </span>
        <h1 data-testid="hero-headline" className="font-outfit text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] mt-6">
          Mash images
          <br />
          into a single, <span className="bg-lilac border-2 border-ink px-2 inline-block rotate-[-1deg]">beautiful</span>
          <br />
          composition.
        </h1>
        <p data-testid="hero-sub" className="mt-8 max-w-xl font-work text-lg text-ink/80 leading-relaxed">
          Upload your PNGs and JPGs, drag them onto a square canvas, layer them up, pick a background, hit export.
          That's it. No accounts, no fluff—just your collage, downloaded.
        </p>

        <div className="mt-10 flex flex-wrap gap-4">
          <button
            data-testid="hero-cta-start"
            onClick={onStart}
            className="neo-btn bg-lilac px-7 py-4 text-base"
          >
            Start composing <ArrowDownRight className="ml-2 h-5 w-5" />
          </button>
          <button
            data-testid="hero-cta-learn"
            onClick={onStart}
            className="neo-btn bg-paper px-7 py-4 text-base"
          >
            See how it works
          </button>
        </div>
      </div>

      {/* Feature strip */}
      <div className="relative z-10 px-6 sm:px-10 lg:px-16 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className="neo-card p-6 hover:-translate-y-1 transition-transform"
              style={{ transform: `rotate(${(i - 1) * 1.2}deg)` }}
            >
              <div className="h-10 w-10 border-2 border-ink bg-mint flex items-center justify-center shadow-brutSm">
                <f.icon className="h-5 w-5" strokeWidth={2.5} />
              </div>
              <h3 className="font-outfit font-bold text-xl mt-4">{f.title}</h3>
              <p className="font-work text-sm text-ink/70 mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
